/**
 * El filtro «Por compras» del paso Audiencia (28/10/2026), puro: el filtro vacío, si dice algo, qué no tiene sentido
 * (lo mismo que rechaza el backend, `util/filtro-compras.util.ts`, para decirlo antes de guardar) y los textos en
 * palabras de comerciante. El bloque es `components/FiltroComprasBloque.tsx`.
 */

import type { FiltroCompras, OpcionesCompras, ResumenCompras } from "../types";
import { num, plural } from "../../campana-nueva/utils/format";
import { ars } from "./formato";
import { diaMes } from "../../base-ventas/logic/base";

export const FILTRO_COMPRAS_VACIO: FiltroCompras = {
  ultimaHaceMasDe: null,
  ultimaHaceMenosDe: null,
  comprasMin: null,
  comprasMax: null,
  ticketMin: null,
  ticketMax: null,
  sucursales: [],
  sinBase: "dejar",
};

/** Lo que dice el bloque cuando no hay base de ventas en uso (el mismo texto que el error del backend). */
export const TEXTO_SIN_BASE_DE_VENTAS = "Subí la base de ventas para filtrar por compras.";
/** Lo que lee una persona de un cliente en el mismo lugar: subirla es del equipo. */
export const TEXTO_SIN_BASE_DE_VENTAS_COMERCIO = "Para filtrar por compras hace falta la base de ventas: la sube el equipo de Deenex.";

/** ¿Dice algo? Sin condiciones y dejando a los que no están en la base, es como no tenerlo (no se manda). */
export function filtroComprasActivo(f: FiltroCompras | null | undefined): f is FiltroCompras {
  if (!f) return false;
  return f.sinBase === "sacar" || f.sucursales.length > 0
    || [f.ultimaHaceMasDe, f.ultimaHaceMenosDe, f.comprasMin, f.comprasMax, f.ticketMin, f.ticketMax].some((x) => x !== null);
}

/** El filtro guardado de una campaña, completo (lo que falte, vacío). */
export function filtroComprasDe(x: Partial<FiltroCompras> | null | undefined): FiltroCompras {
  return { ...FILTRO_COMPRAS_VACIO, ...(x || {}), sucursales: [...(x?.sucursales || [])] };
}

/** Lo que se manda al guardar o contar: el filtro si dice algo, null si no. */
export function filtroParaMandar(f: FiltroCompras | null | undefined): FiltroCompras | null {
  return filtroComprasActivo(f) ? f : null;
}

/**
 * Lo que no tiene sentido, en palabras (vacío si está bien): las mismas combinaciones que no deja guardar el backend,
 * dichas antes de mandar. Un número negativo no llega acá: el campo no lo acepta.
 */
export function errorFiltroCompras(f: FiltroCompras): string {
  if (f.ultimaHaceMasDe !== null && f.ultimaHaceMenosDe !== null && f.ultimaHaceMasDe + 1 >= f.ultimaHaceMenosDe) {
    return `Nadie compró por última vez hace más de ${num(f.ultimaHaceMasDe)} y menos de ${num(f.ultimaHaceMenosDe)} días: revisá los días.`;
  }
  if (f.comprasMin !== null && f.comprasMax !== null && f.comprasMin > f.comprasMax) {
    return "«Compró al menos» no puede ser más que «como mucho»: revisá la cantidad de compras.";
  }
  if (f.ticketMin !== null && f.ticketMax !== null && f.ticketMin > f.ticketMax) {
    return "El ticket promedio mínimo no puede ser más que el máximo.";
  }
  return "";
}

/** Un campo numérico del bloque: vacío → null; si no, el número (entero si `entero`). Lo que no es un número, null. */
export function numeroDelCampo(v: string, entero: boolean): number | null {
  const t = v.trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return entero ? Math.floor(n) : n;
}

/**
 * El filtro en palabras, una condición por renglón (para la cabecera plegada y el detalle): «Compró por última vez hace
 * más de 30 días», «Compró al menos 2 veces», «Gasta en promedio entre $ 5.000 y $ 20.000», «De Palermo o Belgrano»,
 * «Sólo los que están en la base de ventas».
 */
export function describirFiltroCompras(f: FiltroCompras | null | undefined): string[] {
  if (!filtroComprasActivo(f)) return [];
  const out: string[] = [];
  const dias = (n: number) => `${num(n)} ${plural(n, "día", "días")}`;
  const veces = (n: number) => `${num(n)} ${plural(n, "vez", "veces")}`;
  if (f.ultimaHaceMasDe !== null && f.ultimaHaceMenosDe !== null) out.push(`Compró por última vez hace más de ${dias(f.ultimaHaceMasDe)} y menos de ${dias(f.ultimaHaceMenosDe)}`);
  else if (f.ultimaHaceMasDe !== null) out.push(`Compró por última vez hace más de ${dias(f.ultimaHaceMasDe)}`);
  else if (f.ultimaHaceMenosDe !== null) out.push(`Compró por última vez hace menos de ${dias(f.ultimaHaceMenosDe)}`);
  if (f.comprasMin !== null && f.comprasMax !== null) out.push(f.comprasMin === f.comprasMax ? `Compró ${veces(f.comprasMin)}` : `Compró entre ${num(f.comprasMin)} y ${veces(f.comprasMax)}`);
  else if (f.comprasMin !== null) out.push(`Compró al menos ${veces(f.comprasMin)}`);
  else if (f.comprasMax !== null) out.push(`Compró como mucho ${veces(f.comprasMax)}`);
  if (f.ticketMin !== null && f.ticketMax !== null) out.push(`Gasta en promedio entre ${ars(f.ticketMin)} y ${ars(f.ticketMax)}`);
  else if (f.ticketMin !== null) out.push(`Gasta en promedio ${ars(f.ticketMin)} o más`);
  else if (f.ticketMax !== null) out.push(`Gasta en promedio ${ars(f.ticketMax)} o menos`);
  if (f.sucursales.length) out.push(`De ${f.sucursales.length === 1 ? "la sucursal" : "las sucursales"} ${unir(f.sucursales)}`);
  if (f.sinBase === "sacar") out.push("Sólo los que están en la base de ventas");
  return out;
}

/** «Palermo», «Palermo o Belgrano», «Palermo, Belgrano o Centro». */
function unir(xs: string[]): string {
  return xs.length <= 1 ? xs[0] ?? "" : `${xs.slice(0, -1).join(", ")} o ${xs[xs.length - 1]}`;
}

/** «Con este filtro quedan 820 de 1.000 personas.» (y cuántos están en la base de ventas). */
export function textoQuedan(r: ResumenCompras): string {
  const quedan = Math.max(0, r.personas - r.fuera);
  const base = `Con este filtro ${plural(quedan, "queda", "quedan")} ${num(quedan)} de ${num(r.personas)} ${plural(r.personas, "persona", "personas")}`;
  return `${base} (${num(r.enLaBase)} ${plural(r.enLaBase, "está", "están")} en tu base de ventas).`;
}

/** «La base de ventas en uso llega hasta el 26/10: las compras de después no se ven.» (vacío sin fecha). */
export function textoHastaDeLaBase(base: OpcionesCompras["base"]): string {
  const hasta = diaMes(base?.hasta);
  return hasta ? `La base de ventas en uso llega hasta el ${hasta}: las compras de después no se ven.` : "";
}
